import type { AdminApiUsagePeriod } from "@repo/app-types/entities/creditUsage";
import type { MetronomeCoverage } from "@repo/app-types/entities/metronome";
import { DateTime } from "luxon";

/** Usage figures are fractional in the API; whole units everywhere on screen. */
export function formatWholeNumber(value: number): string {
  return Math.round(value).toLocaleString("en-US");
}

/**
 * A usage figure against its allowance — "1,200 of 5,000 credits used", or
 * "1,200 credits used" when no allowance is set.
 */
export function formatUsageAgainstIncluded(
  used: number,
  included: number | null,
  verb: string,
): string {
  return included === null
    ? `${formatWholeNumber(used)} ${verb}`
    : `${formatWholeNumber(used)} of ${formatWholeNumber(included)} ${verb}`;
}

/**
 * A period as "Jul 13 – Aug 12, 2026". `ends_at` is exclusive, so the shown
 * end date is the day before it.
 */
export function formatPeriodLabel(period: AdminApiUsagePeriod): string {
  const start = DateTime.fromISO(period.starts_at, { zone: "utc" });
  const endInclusive = DateTime.fromISO(period.ends_at, { zone: "utc" }).minus({
    days: 1,
  });
  return `${start.toFormat("MMM d")} – ${endInclusive.toFormat("MMM d, yyyy")}`;
}

/** The day the period's allowance resets, i.e. its exclusive `ends_at`. */
export function formatPeriodResetDate(period: AdminApiUsagePeriod): string {
  return DateTime.fromISO(period.ends_at, { zone: "utc" }).toFormat("MMM d");
}

/**
 * What paid for the usage besides the recurring grant, per Metronome's
 * statement — the grant itself is the headline, so only the other sources are
 * named here.
 */
export function formatCoverage(
  coverage: MetronomeCoverage,
  unitNoun: string,
): string[] {
  return [
    coverage.from_purchased_credits > 0
      ? `${formatWholeNumber(coverage.from_purchased_credits)} covered by purchased ${unitNoun}`
      : null,
    coverage.from_promotional_credits > 0
      ? `${formatWholeNumber(coverage.from_promotional_credits)} covered by promotional ${unitNoun}`
      : null,
    coverage.from_other_credits > 0
      ? `${formatWholeNumber(coverage.from_other_credits)} covered by other credits`
      : null,
  ].filter((part) => part !== null);
}
